import {useMemo} from "react";
import {Group, Image, Layer, Text} from "react-konva";
import type {Image as MapImage} from "@/images/Image.ts";
import type {LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import {useSoftFogWorker} from "@/tabletop/visibility/useSoftFogWorker.ts";
import type {FogScene} from "@/tabletop/visibility/SoftFogWorkerProtocol.ts";
import {placeFogFrame} from "@/tabletop/visibility/SoftFogPlacement.ts";
import {editorLightingScene, editorLightingTransform} from "./EditorLightingScene.ts";
import type {VisibilityPolygon} from "@/tabletop/visibility/VisibilityGeometry.ts";

export default function EditorLightingPreview({document, image, transform, size}: {
    document: LevelGeometryDocument;
    image: MapImage;
    transform: {x: number; y: number; scaleX: number};
    size: {width: number; height: number};
}) {
    const polygonCache = useMemo(() => new Map<string, VisibilityPolygon>(), []);
    const lighting = useMemo(() => editorLightingScene(document, image, polygonCache), [document, image, polygonCache]);
    const map = editorLightingTransform(document, transform);
    const anchorX = Math.floor(map.x / 192) * 192;
    const anchorY = Math.floor(map.y / 192) * 192;
    const scene = useMemo<FogScene>(() => ({mode: "LIGHTING", lighting,
        levelId: image.id, areas: [], lights: lighting.lights, blockerKey: lighting.blockerKey,
        radiusMeters: 36, stageSize: {width: size.width + 384, height: size.height + 384},
        transform: {x: anchorX + 192, y: anchorY + 192, scale: map.scale},
    }), [lighting, image.id, size.width, size.height, anchorX, anchorY, map.scale]);
    const {frame, unavailable} = useSoftFogWorker(scene);
    const placement = frame && frame.scene.levelId === image.id ? placeFogFrame(frame.scene, map) : undefined;
    return <Layer listening={false}>
        {unavailable ? <Text x={16} y={16} text="GPU-Lichtvorschau nicht verfügbar" fill="#fca5a5" fontSize={14}/>
            : frame && placement ? <Group clipX={transform.x} clipY={transform.y}
                clipWidth={image.width * transform.scaleX} clipHeight={image.height * transform.scaleX}>
                <Image image={frame.bitmap} {...placement}/>
                <Image image={frame.tintBitmap} {...placement} globalCompositeOperation="lighter"/>
            </Group> : <Text x={16} y={16} text="Lichtvorschau wird geladen …" fill="white" fontSize={14}/>}
    </Layer>;
}
